import moment from "moment";
import companyService from "@/services/company";
import warehouseService from "@/services/warehouse";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import hasPlural from "@/components/report/shared/hasPlural";
import { formatMoney, formatQuantity } from "@/components/report/shared/formatQuantity";
import { formatRange, formatShortDate } from "@/components/report/shared/periods";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import { buildTables, excelSheets, exportFileName, printTables } from "./recipesExport";
import {
  applyEdit,
  count,
  editCount,
  filterCards,
  formatNorm,
  localToday,
  matches,
  mealCalc,
  normalizeReport,
  sortCardEntries,
  sortTotals,
  totalsCalc,
  withoutMeal,
} from "./recipesLogic";
import {
  ALL_RECIPE_SHIFTS,
  DEFAULT_RECIPE_SHIFTS,
  DEFAULT_RECIPE_TAB,
  LEGACY_UI_VALUE,
  MANAGED_RECIPES_QUERY,
  RECIPES_QUERY,
  RECIPE_TABS,
  RECIPE_VIEWS,
  buildRecipePeriodPresets,
  parseRange,
  parseRecipeShifts,
  serializeRecipeShifts,
} from "./recipesQuery";

const ISO = "YYYY-MM-DD";
const TOAST_MS = 3200;
const TOAST_ACTION_MS = 8000;

const TAB_ICONS = { meals: "mdi-silverware-fork-knife", total: "mdi-format-list-bulleted" };

/**
 * Zajedničko stanje stranice Recepti (računar i telefon): izabrani period, smjene, prikaz i jela, učitavanje izvještaja,
 * izmijenjene porcije, izračun kartica i zbirne liste, brojke u zaglavlju, štampa i Excel. Stranica ga drži (a ne ljuska),
 * pa izmjene ne nestaju kad se prozor suzi do telefona i vrati. Ljuske i dijelovi ga dobijaju preko
 * `inject: { ctl: "recipesCtl" }`.
 *
 * Izmjena porcija važi samo na ekranu, u štampi i u Excelu (ne snima se nigdje); period, prikaz i osvježavanje je brišu,
 * pa im prethodi pitanje (`guard`).
 */
export default {
  mixins: [hasPlural, hasPrinting, hasExcel],
  provide() {
    return { recipesCtl: this };
  },
  data: () => ({
    from: localToday(),
    to: localToday(),
    view: "meal",
    defaultView: "meal",
    shifts: [...DEFAULT_RECIPE_SHIFTS],
    price: false,
    search: "",
    off: {},
    edits: {},
    closed: {},
    expanded: {},
    cardOrder: "name",
    sort: { key: "name", dir: 1 },
    showZero: false,
    items: [],
    status: "loading",
    dialog: null,
    toast: null,
  }),
  computed: {
    isPhone() {
      return Boolean(this.device && this.device.isMobile);
    },
    useLegacy() {
      return this.$route.query[RECIPES_QUERY.legacy] === LEGACY_UI_VALUE;
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
    restaurantId() {
      return this.restaurant ? this.restaurant.id : null;
    },
    restaurantName() {
      return this.toLatin((this.restaurant && this.restaurant.name) || "");
    },
    currency() {
      return (this.restaurant && this.restaurant.settings && this.restaurant.settings.price) || "";
    },
    numberLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    presets() {
      return buildRecipePeriodPresets();
    },

    // ------------------------------------------------------------ ekrani
    tabs() {
      return RECIPE_TABS.map((key) => ({
        key,
        icon: TAB_ICONS[key],
        title: this.$t(`recipes_shell.tabs.${key}`),
        badge: this.status === "ready" ? (key === "meals" ? this.cardEntries.length : this.totalsShown.length) || "" : "",
      }));
    },
    menuGroups() {
      return [{ key: "groceries", title: this.$t("recipes_shell.group"), items: this.tabs }];
    },
    menuKeys() {
      return RECIPE_TABS;
    },
    currentKey() {
      const wanted = this.$route.query[RECIPES_QUERY.tab];
      return RECIPE_TABS.includes(wanted) ? wanted : DEFAULT_RECIPE_TAB;
    },

    // ------------------------------------------------------------ podaci i izračun
    ready() {
      return this.status === "ready";
    },
    busy() {
      return this.status === "loading";
    },
    failed() {
      return this.status === "error";
    },
    hasData() {
      return this.ready && this.items.length > 0;
    },
    calcs() {
      const options = { shifts: this.shifts, edits: this.edits };
      return this.items.map((meal) => mealCalc(meal, options));
    },
    picked() {
      return this.calcs.filter((c) => !this.off[c.name]);
    },
    withPortions() {
      return this.picked.filter((c) => c.portions > 0);
    },
    zeroList() {
      return this.picked.filter((c) => c.portions === 0);
    },
    noRecipeList() {
      return this.withPortions.filter((c) => !c.hasRecipe);
    },
    /** Kartice jela koja prolaze pretragu, u izabranom redoslijedu. */
    cardEntries() {
      return sortCardEntries(filterCards(this.withPortions, this.search), this.cardOrder);
    },
    /** Kartice na ekranu: i jela bez porcija u izabranim smjenama, ako je korisnik tražio da se vide. */
    visibleCards() {
      if (!this.showZero) {
        return this.cardEntries;
      }
      return this.cardEntries.concat(this.zeroList.map((c) => ({ c, rows: c.rows, q: "" })));
    },
    totals() {
      return totalsCalc(this.withPortions, this.shifts);
    },
    totalsShown() {
      const query = this.search.trim();
      const found = this.totals.filter((row) => matches(row.name, query));
      return sortTotals(found, this.sort.key, this.sort.dir);
    },
    editTotal() {
      return editCount(this.edits);
    },
    mealOptions() {
      return this.calcs.map((c) => ({ key: c.name, label: this.toLatin(c.name), portions: c.portions }));
    },
    pickedCount() {
      return this.picked.length;
    },
    /** Šta ekran pokazuje umjesto sadržaja: učitavanje, grešku, "nema podataka" ili "ništa ne odgovara" (null = sadržaj). */
    stateKind() {
      if (this.busy) {
        return "loading";
      }
      if (this.failed) {
        return "error";
      }
      if (!this.items.length) {
        return "nodata";
      }
      const shown = this.currentKey === "meals" ? this.visibleCards.length : this.totalsShown.length;
      return shown ? null : "nomatch";
    },
    /** Najmanja širina kartice: naziv + kolona po smjeni + ukupno (+ trošak) + unutrašnji razmak; do tri kolone. */
    cardMinWidth() {
      return 170 + 72 * this.shifts.length + 92 + (this.price ? 90 : 0) + 32;
    },
    /** Veza "Dodaj recept" vodi na stranicu Jela, koju vide samo admin i restoran. */
    canAddRecipe() {
      return ["admin", "restaurant"].includes(this.userProfile && this.userProfile.type);
    },
    allCardsClosed() {
      return this.cardEntries.length > 0 && this.cardEntries.every((e) => this.closed[e.c.name]);
    },
    /** Ekran ima šta da štampa i izveze (dugmad su inače isključena). */
    canExport() {
      return this.hasData && (this.currentKey === "meals" ? this.cardEntries.length > 0 : this.totalsShown.length > 0);
    },

    // ------------------------------------------------------------ zaglavlje
    kpis() {
      if (!this.hasData) {
        return [];
      }
      const money = (value) => [this.fm(value), this.currency].filter(Boolean).join(" ");
      if (this.currentKey === "meals") {
        const kinds = {};
        let portions = 0;
        let cost = 0;
        this.cardEntries.forEach((e) => {
          portions += e.c.portions;
          cost += e.c.cost;
          e.c.rows.forEach((r) => {
            kinds[`${r.name}_${r.measure}`] = true;
          });
        });
        const list = [
          { key: "meals", label: this.$t("recipes_shell.kpi.meals"), value: this.fq(this.cardEntries.length) },
          { key: "portions", label: this.$t("recipes_shell.kpi.portions"), value: this.fq(portions) },
          { key: "groceries", label: this.$t("recipes_shell.kpi.groceries"), value: this.fq(Object.keys(kinds).length) },
        ];
        if (this.price) {
          list.push({ key: "cost", label: this.$t("recipes_shell.cost"), value: money(cost) });
          list.push({ key: "per", label: this.$t("recipes_shell.per_portion"), value: money(portions ? cost / portions : 0) });
        }
        return list;
      }
      const portions = this.withPortions.reduce((total, c) => total + c.portions, 0);
      const cost = this.totalsShown.reduce((total, row) => total + (row.cost || 0), 0);
      const list = [
        { key: "groceries", label: this.$t("recipes_shell.kpi.groceries"), value: this.fq(this.totalsShown.length) },
        { key: "meals", label: this.$t("recipes_shell.kpi.meals"), value: this.fq(this.withPortions.length) },
        { key: "portions", label: this.$t("recipes_shell.kpi.portions"), value: this.fq(portions) },
      ];
      if (this.price) {
        list.push({ key: "cost", label: this.$t("recipes_shell.cost"), value: money(cost) });
      }
      return list;
    },
    periodText() {
      return formatRange(this.from, this.to);
    },
    shiftsText() {
      const list = [...this.shifts].sort();
      if (!list.length) {
        return this.$t("recipes_shell.no_shift");
      }
      return this.$t(list.length === 1 ? "recipes_shell.shift_one" : "recipes_shell.shift_many", { list: list.join(", ") });
    },
    viewText() {
      return this.viewLabel(this.view);
    },
    headerSubtitle() {
      return [this.periodText, this.shiftsText, this.viewText].join(" · ");
    },
    periodShort() {
      const key = this.presets.find((p) => p.from === this.from && p.to === this.to)?.key;
      if (key && this.from !== this.to) {
        return this.$t(`report_mobile.presets.${key}`);
      }
      if (key) {
        return `${this.$t(`report_mobile.presets.${key}`)} · ${formatShortDate(this.from)}`;
      }
      return formatRange(this.from, this.to);
    },
    /** Adresa: samo ono što se razlikuje od podrazumijevanog. */
    urlQuery() {
      const today = localToday();
      const query = {};
      if (this.currentKey !== DEFAULT_RECIPE_TAB) {
        query[RECIPES_QUERY.tab] = this.currentKey;
      }
      if (!(this.from === today && this.to === today)) {
        query[RECIPES_QUERY.from] = this.from;
        query[RECIPES_QUERY.to] = this.to;
      }
      if (this.view !== this.defaultView) {
        query[RECIPES_QUERY.view] = this.view;
      }
      const shifts = serializeRecipeShifts(this.shifts);
      if (shifts) {
        query[RECIPES_QUERY.shifts] = shifts;
      }
      if (this.price) {
        query[RECIPES_QUERY.price] = "1";
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    restaurantId(id, previous) {
      if (id && id !== previous) {
        this.edits = {};
        this.off = {};
        this.load();
      }
    },
  },
  created() {
    this.applyQuery();
    this.start();
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
  },
  methods: {
    // ------------------------------------------------------------ brojevi i nazivi
    fq(value) {
      return formatQuantity(value, this.numberLocale);
    },
    fm(value) {
      return formatMoney(value, this.numberLocale);
    },
    fn(value) {
      return formatNorm(value, this.numberLocale);
    },
    sign(value) {
      return `${value > 0 ? "+" : value < 0 ? "−" : ""}${this.fq(Math.abs(value))}`;
    },
    viewLabel(view) {
      return this.$t(view === "meal" ? "meals_in_meals" : "meal_in_groops");
    },

    // ------------------------------------------------------------ adresa i početak
    applyQuery() {
      const query = this.$route.query;
      const range = parseRange(query[RECIPES_QUERY.from], query[RECIPES_QUERY.to]);
      if (range) {
        [this.from, this.to] = range;
      }
      const shifts = parseRecipeShifts(query[RECIPES_QUERY.shifts]);
      if (shifts) {
        this.shifts = shifts;
      }
      this.price = query[RECIPES_QUERY.price] === "1";
      if (RECIPE_VIEWS.includes(query[RECIPES_QUERY.view])) {
        this.view = query[RECIPES_QUERY.view];
        this.viewFromUrl = true;
      }
    },
    /** Vrsta prikaza po većini firmi: grupne firme → "Jela iz grupa", inače "Jela u obrocima" (kao stara stranica). */
    async start() {
      let detected = "meal";
      try {
        const res = await companyService.getCompanies();
        const companies = (res && res.data) || [];
        const groups = companies.filter((company) => company.type === "group").length;
        detected = groups > companies.length - groups ? "group" : "meal";
      } catch (error) {
        console.error("Error loading companies:", error);
      }
      this.defaultView = detected;
      if (!this.viewFromUrl) {
        this.view = detected;
      }
      this.load();
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_RECIPES_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
    /** Stara stranica: ?ui=stari. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [RECIPES_QUERY.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
    useNewView() {
      const query = { ...this.$route.query };
      delete query[RECIPES_QUERY.legacy];
      this.$router.replace({ query }).catch(() => {});
    },

    // ------------------------------------------------------------ učitavanje
    async load() {
      const id = this.restaurantId;
      if (!id) {
        this.items = [];
        this.status = "ready";
        return;
      }
      const token = (this.loadToken = (this.loadToken || 0) + 1);
      this.status = "loading";
      try {
        const params = { date_from: this.from, date_to: this.to, price: true };
        const res =
          this.view === "meal"
            ? await warehouseService.getDailyWarehouseReport(id, params)
            : await warehouseService.getDailyGroupWarehouseReport(id, params);
        if (token !== this.loadToken) {
          return;
        }
        const body = res && res.data;
        this.items = normalizeReport(Array.isArray(body) ? body : body && body.data);
        this.status = "ready";
      } catch (error) {
        if (token !== this.loadToken) {
          return;
        }
        console.error("Error loading recipes report:", error);
        this.items = [];
        this.status = "error";
      }
    },
    /** Novi podaci: izmijenjene porcije se brišu (važe samo za prikazane narudžbe). */
    reloadFresh() {
      this.edits = {};
      this.load();
    },
    reload() {
      this.guard(() => this.reloadFresh(), "refresh");
    },
    retry() {
      this.reloadFresh();
    },

    // ------------------------------------------------------------ izbori
    /** Prije radnje koja briše izmijenjene porcije pita korisnika; bez izmjena odmah izvršava. */
    guard(apply, why) {
      if (!this.editTotal) {
        apply();
        return;
      }
      this.dialog = { why, run: apply };
    },
    confirmDialog() {
      const dialog = this.dialog;
      this.dialog = null;
      if (dialog && dialog.run) {
        dialog.run();
      }
    },
    cancelDialog() {
      this.dialog = null;
    },
    setPeriod({ from, to }) {
      if (from === this.from && to === this.to) {
        return;
      }
      this.guard(() => {
        this.from = from;
        this.to = to;
        this.reloadFresh();
      }, "period");
    },
    shiftDay(step) {
      const move = (date) => moment(date, ISO).add(step, "day").format(ISO);
      this.guard(() => {
        this.from = move(this.from);
        this.to = move(this.to);
        this.reloadFresh();
      }, "day");
    },
    setView(view) {
      if (view === this.view || !RECIPE_VIEWS.includes(view)) {
        return;
      }
      this.guard(() => {
        this.view = view;
        this.reloadFresh();
      }, "view");
    },
    setShifts(list) {
      this.shifts = [...list].filter((n) => ALL_RECIPE_SHIFTS.includes(n)).sort();
    },
    toggleShift(shift) {
      this.setShifts(this.shifts.includes(shift) ? this.shifts.filter((n) => n !== shift) : [...this.shifts, shift]);
    },
    togglePrice() {
      this.price = !this.price;
    },
    setSearch(value) {
      this.search = value;
    },
    isMealOn(name) {
      return !this.off[name];
    },
    toggleMeal(name) {
      const next = { ...this.off };
      if (next[name]) {
        delete next[name];
      } else {
        next[name] = true;
      }
      this.off = next;
    },
    setAllMeals(on) {
      const next = {};
      if (!on) {
        this.calcs.forEach((c) => {
          next[c.name] = true;
        });
      }
      this.off = next;
    },
    setCardOrder(order) {
      this.cardOrder = order;
    },
    sortBy(key) {
      this.sort = this.sort.key === key ? { key, dir: -this.sort.dir } : { key, dir: key === "name" ? 1 : -1 };
    },
    toggleZero() {
      this.showZero = !this.showZero;
    },
    openTab(key) {
      if (!RECIPE_TABS.includes(key) || key === this.currentKey) {
        return;
      }
      this.$router.push({ query: { ...this.$route.query, [RECIPES_QUERY.tab]: key } }).catch(() => {});
    },

    // ------------------------------------------------------------ kartice
    isClosed(name) {
      return Boolean(this.closed[name]);
    },
    toggleCard(name) {
      this.closed = { ...this.closed, [name]: !this.closed[name] };
    },
    toggleAllCards() {
      const next = {};
      if (!this.allCardsClosed) {
        this.cardEntries.forEach((e) => {
          next[e.c.name] = true;
        });
      }
      this.closed = next;
    },
    /** Telefon: korisnikov izbor otvoreno / sklopljeno za karticu (dok ga nema, odlučuje RecipesMobileCard). */
    setExpanded(name, open) {
      this.expanded = { ...this.expanded, [name]: open };
    },

    addRecipe() {
      this.$router.push({ name: "jela" }).catch(() => {});
    },

    // ------------------------------------------------------------ izmjene porcija
    orderedCount(name, shift) {
      const meal = this.items.find((m) => m.name === name);
      const entry = meal && meal.shifts.find((s) => s.shift === shift);
      return entry ? count(entry.quantity) : 0;
    },
    editPortions(name, shift, value) {
      this.edits = applyEdit(this.edits, name, shift, value, this.orderedCount(name, shift));
    },
    revertMeal(name) {
      this.edits = withoutMeal(this.edits, name);
      this.notify(this.$t("recipes_shell.reverted_meal", { name: this.toLatin(name) }));
    },
    revertAll() {
      const n = this.editTotal;
      this.edits = {};
      this.notify(this.pluralT("recipes_shell.reverted_n", n));
    },

    // ------------------------------------------------------------ namirnica (panel i list)
    ingredientByKey(key) {
      return this.totals.find((row) => row.key === key) || null;
    },
    async copyIngredient(row) {
      if (!row) {
        return;
      }
      const headers = [
        this.$t("recipes_shell.meal"),
        this.$t("recipes_shell.portions"),
        this.$t("recipes_shell.norm"),
        this.$t("recipes_shell.quantity"),
      ];
      const rows = row.parts.map((p) => [
        this.toLatin(p.meal),
        p.counts.reduce((a, b) => a + b, 0),
        this.fn(p.norm),
        `${this.fq(p.exactTotal)} ${row.measure}`.trim(),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.notify(this.$t(copied ? "recipes_shell.copied" : "recipes_shell.copy_failed"));
    },

    // ------------------------------------------------------------ obavještenja
    notify(text, { label = "", action = null, duration = 0 } = {}) {
      clearTimeout(this.toastTimer);
      this.toast = { text, label, action };
      this.toastTimer = setTimeout(
        () => {
          this.toast = null;
        },
        duration || (action ? TOAST_ACTION_MS : TOAST_MS)
      );
    },
    runToastAction() {
      const { action } = this.toast || {};
      this.toast = null;
      if (typeof action === "function") {
        action();
      }
    },

    // ------------------------------------------------------------ štampa i Excel
    exportContext(excel) {
      return {
        excel,
        shifts: [...this.shifts].sort(),
        price: this.price,
        currency: this.currency,
        t: (key, params) => this.$t(key, params),
        plural: (key, n) => this.pluralT(key, n),
        name: (text) => this.toLatin(text),
        qty: (value) => this.fq(value),
        money: (value) => this.fm(value),
      };
    },
    exportTables(excel) {
      return buildTables(this.exportContext(excel), {
        tab: this.currentKey,
        cards: this.cardEntries,
        totals: this.totalsShown,
        editCount: this.editTotal,
      });
    },
    editedNote() {
      return this.editTotal ? this.pluralT("recipes_shell.edited_note", this.editTotal) : "";
    },
    print() {
      if (!this.canExport) {
        return;
      }
      const note = this.editedNote();
      this.setPrinter();
      this.printDocument({
        pageTitle: this.$t("recipes"),
        title: this.$t("recipes"),
        subtitle: [this.periodText, this.shiftsText, this.viewText].join(" · "),
        beforeTableHtml: note ? `<div style="text-align:center;margin:4px 0 8px;font-size:13px;">${note}</div>` : "",
        tables: printTables(this.exportTables(false)),
      });
    },
    async excel() {
      if (!this.canExport) {
        return;
      }
      this.setExcel();
      const sheets = excelSheets(this.exportTables(true), {
        subtitle: [this.periodText, this.shiftsText, this.viewText].join(" · "),
        editedNote: this.editedNote(),
      });
      const done = await this.downloadExcel({ fileName: exportFileName(this.from, this.to), sheets });
      if (done === false) {
        this.notify(this.$t("recipes_shell.excel_failed"));
      }
    },
  },
};
